import { IconCog, IconSearch } from '@loadsmart/icons'
import { Select } from '@loadsmart/loadsmart-ui'
import { useCallback } from 'react'
import { ActionableTag, Button, Layout, Table } from '@loadsmart/miranda-react'
import { useFormik } from 'formik'
import styled from 'styled-components'
import { trim } from 'lodash'

import { GateFeedFilter } from '../../../clients/type'
import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../../../util/analytics'
import { Location, SearchForm } from '../../../models/client/reports'
import { TABLE_FILTER_OPTIONS } from '../../constants/reports'

type TableFiltersProps = {
  handleSearchCategoryChange: (category) => void
  handleOpenDrawer: () => void
  selectedLocation: Location
  selectedCategory: any
  filters: GateFeedFilter[]
  onAddFilter: (filter: GateFeedFilter) => void
  onRemoveFilter: (filter: GateFeedFilter) => void
  onResetFilters: () => void
  isLoading: boolean
}

const StyledGroup = styled(Layout.Group)`
  width: 80%;
`

const StyledSelect = styled(Select)`
  max-width: 180px;
`

const Input = styled.input`
  padding: 0.7rem;
  border: 1px solid rgb(230, 237, 242);
  border-radius: 4px;
  outline: none;
  max-width: 220px;

  &:focus {
    border-color: rgb(61, 74, 85);
  }
`

export function TableFilters({
  handleSearchCategoryChange,
  handleOpenDrawer,
  selectedLocation,
  selectedCategory,
  filters,
  onAddFilter,
  onRemoveFilter,
  onResetFilters,
  isLoading,
}: TableFiltersProps) {
  const addBaseFilters = () => {
    onAddFilter({
      type: 'site',
      id: selectedLocation.value,
      label: 'Site ID',
      value: {
        label: 'Current site',
        value: selectedLocation.value || '',
      },
    })
  }

  const onSubmit = useCallback(
    (formData: SearchForm) => {
      if (!formData.searchTerm) return

      clearSearch()

      const trimmedSearchTerm = trim(formData.searchTerm)

      onAddFilter({
        type: 'property',
        id: selectedCategory.value,
        label: selectedCategory.label,
        category: selectedCategory.category,
        value: [{ name: selectedCategory.value, value: trimmedSearchTerm }],
      })

      analytics.track(AnalyticsEvent.ReportsSearchAsset, AnalyticsEventTrigger.select, {
        searchString: formData.searchTerm,
        category: selectedCategory.label,
      })
    },
    [selectedCategory, selectedLocation]
  )

  const { values, setFieldValue, handleChange, handleSubmit } = useFormik<SearchForm>({
    initialValues: {
      searchTerm: '',
    },
    onSubmit,
  })

  const clearSearch = () => {
    onResetFilters()
    addBaseFilters()
    setFieldValue('searchTerm', '')
  }

  // get filter property value
  const getSearchFilter = useCallback(() => {
    if (!filters?.length) {
      return null
    }

    const activeFilter = filters
      .filter((filter) => filter.type === 'property')
      .map((filter) => filter)

    return activeFilter?.length ? (
      <ActionableTag onRemove={clearSearch} removable>
        {activeFilter[0]?.label}: {activeFilter[0]?.value[0].value}
      </ActionableTag>
    ) : null
  }, [filters, onAddFilter, onSubmit])

  return (
    <Table.Toolbar>
      <Layout.Group align="center" justify="space-between">
        <StyledGroup>
          <form onSubmit={handleSubmit}>
            <Layout.Group gap="spacing-2">
              <Input
                type="text"
                placeholder="Search"
                name="searchTerm"
                value={values.searchTerm}
                onChange={handleChange}
                disabled={isLoading}
              />
              <Button variant="secondary" type="submit" disabled={!values.searchTerm}>
                <IconSearch width="18" height="18" />
              </Button>
            </Layout.Group>
          </form>

          <StyledSelect
            name="category"
            options={TABLE_FILTER_OPTIONS}
            value={{ label: 'Carrier name', value: 'vehicle.carrier.name' }}
            placeholder="Category"
            onChange={handleSearchCategoryChange}
            hideClear
            disabled={isLoading}
          />

          <Layout.Group align="center">{getSearchFilter()}</Layout.Group>
        </StyledGroup>

        <Button
          variant="tertiary"
          type="button"
          leading={<IconCog width="18" height="18" />}
          onClick={handleOpenDrawer}
          disabled={isLoading}
        >
          Table settings
        </Button>
      </Layout.Group>
    </Table.Toolbar>
  )
}
